import { useAuftragRepository } from '@Hooks/useRepository';
import { type ApiContainerAction } from '@Model';
import { ContainerActionContentRepository } from '@Repositories/Auftragsbearbeitung';
import { isOk, type Result, useToasts } from '@synqo/frontend-component-library';
import { useCallback, useState } from 'react';

export interface ContainerMaterial {
    /** Sets the material on each action in turn and reports whether every one took it. */
    chooseMaterial: (actionIds: string[], materialId: string) => Promise<boolean>;
    /** Takes the material off each action in turn and reports whether every one let it go. */
    clearMaterial: (actionIds: string[], materialId: string) => Promise<boolean>;
    /** Whether a material is being written. */
    isChoosing: boolean;
}

/**
 * Sets the material of container actions through the content link of each action.
 *
 * A row that takes the material is merged into the order as it answers, so a run that stops at a
 * refusal leaves the rows before it changed.
 */
// @ai-generated 2026-10-08T20:07:16+00:00 by matthias.gessinger@synqony.com using Claude Sonnet 5.5
export function useContainerMaterial(
    orderId: string,
    mergeAction: (action: ApiContainerAction) => void,
): ContainerMaterial {
    const content = useAuftragRepository(ContainerActionContentRepository);
    const { pushAllToasts } = useToasts();
    const [isChoosing, setIsChoosing] = useState(false);

    const eachAction = useCallback(
        async (actionIds: string[], write: (actionId: string) => Promise<Result<ApiContainerAction>>) => {
            setIsChoosing(true);
            try {
                for (const actionId of actionIds) {
                    const result = await write(actionId);

                    pushAllToasts(result.messages);

                    if (!isOk(result)) {
                        return false;
                    }

                    mergeAction(result.data);
                }

                return true;
            } finally {
                setIsChoosing(false);
            }
        },
        [mergeAction, pushAllToasts],
    );

    const chooseMaterial = useCallback(
        (actionIds: string[], materialId: string) =>
            eachAction(actionIds, (actionId) => content.addLinkedObject(materialId, actionId, orderId)),
        [eachAction, content, orderId],
    );

    const clearMaterial = useCallback(
        (actionIds: string[], materialId: string) =>
            eachAction(actionIds, (actionId) => content.deleteLinkedObject(materialId, actionId, orderId)),
        [eachAction, content, orderId],
    );

    return { chooseMaterial, clearMaterial, isChoosing };
}
